import React, { type FunctionComponent } from 'react'
import isNumber from 'lodash/isNumber'
import styled from '@emotion/styled'
import { colors, fontSize, spacing } from '@retailer-platform/shared-components'
import { InformationIcon } from '@instacart/ids-core'
import { useDomainMessages } from 'domains/aggregate-store-performance/utils/domain/intl'
import { type HistoricalStorePerformanceOverviewMetrics } from 'domains/aggregate-store-performance/types/AggregateStorePerformance.types'
import { PerformanceMetric } from 'gin-and-tonic/containers/picking-performance-overview'

const Container = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  flexGrow: 1,
  margin: `${spacing.X24} ${spacing.X16}`,
})

interface EmptyMetricsProps {
  fullSize: boolean
}

const EmptyMetrics = styled.div<EmptyMetricsProps>(({ fullSize }) => ({
  marginTop: spacing.X8,
  paddingTop: spacing.X4,
  borderTop: `1px solid ${colors.GRAYSCALE.X30}`,
  gridRow: '2',
  gridColumn: fullSize ? '1 / span 6' : '4 / span 3',
  svg: {
    fill: colors.GRAYSCALE.X30,
  },
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  fontSize: fontSize.X12,
}))

const EmptyMetricsMessage = styled.div({
  color: colors.GRAYSCALE.X50,
  fontSize: fontSize.X10,
  lineHeight: '14px',
  marginLeft: spacing.X4,
})

interface HistoricalStorePerformanceMetricsProps extends HistoricalStorePerformanceOverviewMetrics {
  isFirstDayOfCurrentWeek: boolean
  hasStoresSelected: boolean
}

export const HistoricalStorePerformanceMetrics: FunctionComponent<
  HistoricalStorePerformanceMetricsProps
> = ({
  hoursScheduled,
  hoursRecommended,
  totalHoursTrend,
  scheduleGaps,
  scheduleGapsTrend,
  matchRate,
  matchRateTrend,
  dedicatedMatchRate,
  dedicatedMatchRateTrend,
  utilization,
  utilizationTrend,
  dedicatedUtilization,
  dedicatedUtilizationTrend,
  fiveHourAvailability,
  fiveHourAvailabilityTrend,
  fulfilledOrders,
  fulfilledOrdersTrend,
  isFirstDayOfCurrentWeek,
  hasStoresSelected,
}) => {
  const messages = useDomainMessages({
    totalHours: 'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.totalHours',
    totalHoursTooltip:
      'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.totalHoursTooltip',
    scheduleGaps: 'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.scheduleGaps',
    scheduleGapsTooltip:
      'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.scheduleGapsTooltip',
    matchRate: 'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.matchRate',
    matchRateTooltip:
      'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.matchRateTooltip',
    dedicatedMatchRate:
      'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.dedicatedMatchRate',
    dedicatedMatchRateTooltip:
      'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.dedicatedMatchRateTooltip',
    utilization: 'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.utilization',
    utilizationTooltip:
      'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.utilizationTooltip',
    dedicatedUtilization:
      'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.dedicatedUtilization',
    dedicatedUtilizationTooltip:
      'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.dedicatedUtilizationTooltip',
    fiveHourAvailability:
      'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.fiveHourAvailability',
    fiveHourAvailabilityTooltip:
      'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.fiveHourAvailabilityTooltip',
    fulfilledOrders:
      'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.fulfilledOrders',
    fulfilledOrdersTooltip:
      'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.fulfilledOrdersTooltip',
    emptyMetricsFirstDayOfWeek:
      'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.emptyMetrics.firstDayOfWeek',
    emptyMetricsFutureWeek:
      'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.emptyMetrics.futureWeek',
    dedicatedTooltipDisclaimer:
      'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.dedicatedTooltipDisclaimer',
    noStoresSelected:
      'aggregateStorePerformanceDomain.historicalStorePerformanceMetrics.emptyMetrics.noStoresSelected',
  })

  const historicalMetricsEmpty =
    (!isNumber(utilization) || !isNumber(dedicatedUtilization)) &&
    !isNumber(fiveHourAvailability) &&
    !isNumber(fulfilledOrders) &&
    (hoursScheduled || hoursRecommended || scheduleGaps || matchRate)

  let emptyMessage = undefined
  if (!hasStoresSelected) {
    emptyMessage = messages.noStoresSelected
  } else if (historicalMetricsEmpty) {
    emptyMessage = isFirstDayOfCurrentWeek
      ? messages.emptyMetricsFirstDayOfWeek
      : messages.emptyMetricsFutureWeek
  }

  return (
    <Container>
      <PerformanceMetric
        title={messages.totalHours}
        tooltip={messages.totalHoursTooltip}
        metric={hoursScheduled}
        metricDenominator={hoursRecommended}
        trendValue={totalHoursTrend?.value}
        trendDirection={totalHoursTrend?.direction}
      />
      <PerformanceMetric
        title={messages.scheduleGaps}
        tooltip={messages.scheduleGapsTooltip}
        metric={scheduleGaps}
        trendValue={scheduleGapsTrend?.value}
        trendDirection={scheduleGapsTrend?.direction}
        invertTrendColor
      />
      {dedicatedMatchRate === undefined ? (
        <PerformanceMetric
          title={messages.matchRate}
          tooltip={messages.matchRateTooltip}
          metric={matchRate}
          metricSymbol={'%'}
          trendValue={matchRateTrend?.value}
          trendDirection={matchRateTrend?.direction}
        />
      ) : (
        <PerformanceMetric
          title={messages.dedicatedMatchRate}
          tooltip={messages.dedicatedMatchRateTooltip}
          tooltipDisclaimer={messages.dedicatedTooltipDisclaimer}
          metric={dedicatedMatchRate}
          metricSymbol={'%'}
          trendValue={dedicatedMatchRateTrend?.value}
          trendDirection={dedicatedMatchRateTrend?.direction}
        />
      )}
      {dedicatedUtilization === undefined ? (
        <PerformanceMetric
          title={messages.utilization}
          tooltip={messages.utilizationTooltip}
          metric={utilization}
          metricSymbol={'%'}
          trendValue={historicalMetricsEmpty ? undefined : utilizationTrend?.value}
          trendDirection={utilizationTrend?.direction}
        />
      ) : (
        <PerformanceMetric
          title={messages.dedicatedUtilization}
          tooltip={messages.dedicatedUtilizationTooltip}
          tooltipDisclaimer={messages.dedicatedTooltipDisclaimer}
          metric={dedicatedUtilization}
          metricSymbol={'%'}
          trendValue={historicalMetricsEmpty ? undefined : dedicatedUtilizationTrend?.value}
          trendDirection={dedicatedUtilizationTrend?.direction}
        />
      )}
      <PerformanceMetric
        title={messages.fiveHourAvailability}
        tooltip={messages.fiveHourAvailabilityTooltip}
        metric={fiveHourAvailability}
        metricSymbol={'%'}
        trendValue={historicalMetricsEmpty ? undefined : fiveHourAvailabilityTrend?.value}
        trendDirection={fiveHourAvailabilityTrend?.direction}
      />
      <PerformanceMetric
        title={messages.fulfilledOrders}
        tooltip={messages.fulfilledOrdersTooltip}
        metric={fulfilledOrders}
        trendValue={historicalMetricsEmpty ? undefined : fulfilledOrdersTrend?.value}
        trendDirection={fulfilledOrdersTrend?.direction}
      />
      {emptyMessage && (
        <EmptyMetrics fullSize={!hasStoresSelected}>
          <InformationIcon style={{ flexShrink: 0 }} />
          <EmptyMetricsMessage>{emptyMessage}</EmptyMetricsMessage>
        </EmptyMetrics>
      )}
    </Container>
  )
}
